import { enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { ConfigService } from '@cue-admin/config';
import { appConfig } from './app.config';
import { AppComponent } from './app/app.component';
import { env } from './environments/environment';
import { registerLocaleData } from '@angular/common';

const AVAILABLE_LOCALE_IDS = {
  en: 'en',
  cs: 'cs',
} as const;
type LocaleModule = { default: any };
type LocaleCode = keyof typeof AVAILABLE_LOCALE_IDS;
type LocaleLoader = () => [Promise<LocaleModule>, Promise<unknown>];
const AVAILABLE_LOCALE_LOADERS: Record<LocaleCode, LocaleLoader> = {
  en: () => [import('@angular/common/locales/en'), import('@progress/kendo-angular-intl/locales/en/all')],
  cs: () => [import('@angular/common/locales/cs'), import('@progress/kendo-angular-intl/locales/cs/all')],
};

function normalizeCode(language: string) {
  return language.toLowerCase().split('-')[0];
}
const loadedLocaleCodes = new Set<string>();

function getLocaleCode(language: string | null | undefined): LocaleCode {
  if (!language) {
    return 'en';
  }

  const normalizedCode = normalizeCode(language);
  if (normalizedCode in AVAILABLE_LOCALE_IDS) {
    return normalizedCode as LocaleCode;
  }

  return 'en';
}

function getPreferredLanguageCode(): LocaleCode {
  const langFromLocalStorage = localStorage.getItem('currentLanguage');
  if (langFromLocalStorage) {
    return getLocaleCode(langFromLocalStorage);
  }

  const userLang = navigator.language || (navigator as Navigator & { userLanguage?: string }).userLanguage;
  return getLocaleCode(userLang);
}

export async function ensureLocaleDataLoaded(language: string | null | undefined) {
  const localeCode = getLocaleCode(language);

  if (loadedLocaleCodes.has(localeCode)) {
    return AVAILABLE_LOCALE_IDS[localeCode];
  }

  const [angularLocaleData] = await Promise.all(AVAILABLE_LOCALE_LOADERS[localeCode]());
  registerLocaleData(angularLocaleData.default, AVAILABLE_LOCALE_IDS[localeCode]);
  loadedLocaleCodes.add(localeCode);

  return AVAILABLE_LOCALE_IDS[localeCode];
}

if (env.production) {
  enableProdMode();
}

const configService = new ConfigService();

const bootstrap = () => bootstrapApplication(AppComponent, appConfig(configService));

configService.load().then(
  () => {
    return ensureLocaleDataLoaded(getPreferredLanguageCode()).then(() => bootstrap());
  },
  (reject: any) => {
    console.error('Cannot load start the application');
    window.alert(reject);
  }
);
